if (!customElements.get('bundle-selector')) {
  customElements.define(
    'bundle-selector',
    class BundleSelector extends HTMLElement {
      constructor() {
        super();
        this.options = Array.from(this.querySelectorAll('.bundle-selector__option'));
        this.radios = Array.from(this.querySelectorAll('.bundle-selector__radio'));
        this.gifts = Array.from(this.querySelectorAll('[data-gift]'));
        this.submitButton = this.querySelector('.bundle-selector__submit');
        this.submitLabel = this.querySelector('.bundle-selector__submit-label');
        this.errorBox = this.querySelector('.bundle-selector__error');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const hasBundle = this.radios.some((r) => r.value === 'bundle');
        this.choice = hasBundle ? this.dataset.default || 'bundle' : 'single';

        this.radios.forEach((radio) => radio.addEventListener('change', () => this.select(radio.value)));
        this.gifts.forEach((gift) =>
          gift.addEventListener('click', () => {
            // A locked gift jumps straight to the bundle.
            if (gift.classList.contains('is-locked') && hasBundle) this.select('bundle');
          })
        );
        this.submitButton.addEventListener('click', this.addToCart.bind(this));

        // Keep in sync when the variant picker changes the main product variant.
        document.addEventListener('change', (evt) => {
          const input = evt.target.closest && evt.target.closest('.product-variant-id');
          if (input) this.updateMainVariant(input.value);
        });

        this.select(this.choice);
      }

      select(choice) {
        this.choice = choice;
        this.radios.forEach((r) => (r.checked = r.value === choice));
        this.options.forEach((o) => o.classList.toggle('is-active', o.dataset.option === choice));
        this.gifts.forEach((gift) => {
          const locked = choice === 'single' && gift.classList.contains('bundle-selector__gift--lockable');
          gift.classList.toggle('is-locked', locked);
          gift.classList.toggle('is-unlocked', !locked);
          gift.setAttribute('aria-pressed', String(!locked));
        });
        this.submitLabel.textContent =
          choice === 'bundle' ? this.submitButton.dataset.bundleLabel : this.submitButton.dataset.singleLabel;
      }

      updateMainVariant(newId) {
        const oldId = this.dataset.mainVariant;
        if (!newId || newId === oldId) return;
        const keys = this.dataset.bundleMain === oldId ? ['singleIds', 'bundleIds'] : ['singleIds'];
        if (this.dataset.bundleMain === oldId) this.dataset.bundleMain = newId;
        keys.forEach((key) => {
          this.dataset[key] = this.dataset[key]
            .split(',')
            .map((id) => (id === oldId ? newId : id))
            .join(',');
        });
        this.dataset.mainVariant = newId;
      }

      currentItems() {
        const ids = (this.choice === 'bundle' ? this.dataset.bundleIds : this.dataset.singleIds)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        // Shopify lists the most recently added line first, so add gifts first and the main product last.
        return ids.reverse().map((id) => ({ id: Number(id), quantity: 1 }));
      }

      showError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message || '';
        this.errorBox.toggleAttribute('hidden', !message);
      }

      addToCart() {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.showError();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const body = { items: this.currentItems() };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        const code = (this.dataset.bundleDiscount || '').trim();
        const useCode = this.choice === 'bundle' && code;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status || !useCode) return response;
            // Apply the discount code to the cart itself so the cart/drawer shows the discounted prices.
            const updateConfig = fetchConfig('javascript');
            updateConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            const updateBody = { discount: code };
            if (this.cart) {
              updateBody.sections = body.sections;
              updateBody.sections_url = body.sections_url;
            }
            updateConfig.body = JSON.stringify(updateBody);
            return fetch(`${routes.cart_update_url}`, updateConfig)
              .then((r) => r.json())
              .then((updated) => {
                // Fallback for stores where update.js ignores the code: set the checkout cookie.
                fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
                if (updated && updated.sections) response.sections = updated.sections;
                return response;
              })
              .catch(() => response);
          })
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // The notification shows one product: pick the main product's line.
            const mainId = Number(
              this.choice === 'bundle' && this.dataset.bundleMain ? this.dataset.bundleMain : this.dataset.mainVariant
            );
            const items = response.items || [response];
            const mainLine = items.find((i) => i.variant_id === mainId) || items[0];
            response.key = mainLine.key;

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainId,
              cartData: response,
            });
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.showError(window.cartStrings?.error || 'Something went wrong.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }
    }
  );
}
